import React, { useState } from "react";
import axios from "axios";
import { X, Diamond, Globe, Gem, Search, FlaskConical, BookOpen, ShieldCheck, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubscribeModal = ({ open, onClose, onRequireAuth }) => {
  const { lang, setLang, t } = useLang();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!user) {
      onClose?.();
      onRequireAuth?.(lang === "fr" ? "Créez un compte pour vous abonner" : "Create an account to subscribe");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "gem_annual_eur",
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = data.checkout_url;
    } catch (e) {
      setLoading(false);
      if (e?.response?.status === 401) {
        onClose?.();
        onRequireAuth?.(lang === "fr" ? "Session expirée, reconnectez-vous" : "Session expired, please sign in");
      } else {
        console.error(e);
      }
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="subscribe-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/85 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl my-auto rounded-sm bg-[#050B1E] border border-[#C1A661]/25 p-6 md:p-10"
        style={{ boxShadow: "inset 0 0 120px rgba(193,166,97,0.05)" }}
      >
        {/* Close */}
        <button
          data-testid="modal-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 md:top-6 md:right-6 text-[#A1A7B3] hover:text-[#F8F5EE] transition-colors z-10"
          aria-label={t.modal.close}
        >
          <X size={22} strokeWidth={1.4} />
        </button>

        {/* Top row: expert data (left) + lang switcher (right) */}
        <div className="flex items-start justify-between gap-4 mb-4 md:mb-6">
          <div className="flex items-start gap-3 max-w-[260px]" data-testid="modal-trust">
            <ShieldCheck size={22} className="text-[#C1A661] mt-0.5 shrink-0" strokeWidth={1.3} />
            <div>
              <div className="text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[#C1A661] font-medium">
                {t.modal.expertHeading}
              </div>
              <p className="mt-1 text-[11px] leading-snug text-[#A1A7B3] font-light">
                {t.modal.expertSub}
              </p>
            </div>
          </div>

          <div className="flex rounded-full border border-[#C1A661]/25 overflow-hidden" data-testid="modal-lang-switcher">
            <button
              data-testid="modal-lang-fr-btn"
              onClick={() => setLang("fr")}
              className={`px-4 py-1.5 text-xs tracking-[0.2em] font-medium transition-colors duration-200 ${
                lang === "fr" ? "bg-[#C1A661]/15 text-[#E2CFA4]" : "text-[#A1A7B3] hover:text-[#E2CFA4]"
              }`}
            >
              <span className="mr-1.5">🇫🇷</span>FR
            </button>
            <div className="w-px bg-[#C1A661]/25" />
            <button
              data-testid="modal-lang-en-btn"
              onClick={() => setLang("en")}
              className={`px-4 py-1.5 text-xs tracking-[0.2em] font-medium transition-colors duration-200 ${
                lang === "en" ? "bg-[#C1A661]/15 text-[#E2CFA4]" : "text-[#A1A7B3] hover:text-[#E2CFA4]"
              }`}
            >
              <span className="mr-1.5">🇬🇧</span>EN
            </button>
          </div>
        </div>

        {/* Brand */}
        <div className="flex flex-col items-center text-center">
          <Diamond size={30} strokeWidth={1.2} className="text-[#C1A661] mb-2" />
          <h2
            className="font-serif font-light text-4xl md:text-5xl text-[#E2CFA4]"
            style={{ letterSpacing: "0.15em" }}
          >
            {t.heroTitle}
          </h2>
          <p className="mt-2 text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#C1A661] font-medium">
            {t.heroSubtitle}
          </p>
        </div>

        {/* Heading */}
        <div className="mt-6 md:mt-8 text-center">
          <h3 className="text-2xl md:text-3xl text-[#F8F5EE] font-light">{t.modal.title}</h3>
          <p className="mt-2 text-sm text-[#A1A7B3] font-light">{t.modal.subtitle}</p>
        </div>

        {/* Offer card */}
        <div className="mt-8 flex justify-center">
          <div
            data-testid="offer-card"
            className="relative w-full max-w-md border-2 border-[#C1A661] rounded-md px-8 pt-10 pb-8 bg-[#050B1E]"
            style={{ boxShadow: "0 0 40px rgba(193,166,97,0.15)" }}
          >
            {/* Badge */}
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#C1A661] px-5 py-1.5 rounded-sm">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#050B1E] font-semibold">
                {t.modal.bestOffer}
              </span>
            </div>

            <div className="text-center">
              <div className="text-[11px] tracking-[0.35em] uppercase text-[#A1A7B3] font-light">
                {t.modal.annualLabel}
              </div>
              <div
                data-testid="subscribe-monthly-equiv"
                className="mt-2 text-[11px] tracking-[0.25em] uppercase text-[#E2CFA4] font-medium"
              >
                {t.modal.monthlyEquiv}
              </div>
              <div className="mt-1 font-serif text-6xl md:text-7xl text-[#C1A661] font-light leading-none">
                €79.00
              </div>
              <div className="mt-2 text-xs tracking-[0.2em] text-[#A1A7B3] pb-4 border-b border-[#C1A661]/30">
                {t.modal.perYear}
              </div>

              <div
                data-testid="subscribe-unlimited-note"
                className="mt-5 text-[12px] md:text-[13px] text-[#E2CFA4] font-light leading-relaxed"
              >
                {t.modal.unlimitedAccess}
              </div>

              <button
                data-testid="subscribe-confirm-btn"
                onClick={handleSubscribe}
                disabled={loading}
                className="mt-6 w-full py-3.5 rounded-sm bg-[#C1A661] hover:bg-[#E2CFA4] disabled:opacity-60 text-[#050B1E] text-sm tracking-[0.15em] font-semibold transition-colors duration-200 flex items-center justify-center gap-2.5"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t.modal.loading}
                  </>
                ) : (
                  t.modal.choose
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Included */}
        <div className="mt-10 border border-[#C1A661]/25 rounded-sm p-6 md:p-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="text-[#C1A661] text-xs">◆</span>
            <span className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#C1A661] font-medium">
              {t.modal.includedTitle}
            </span>
            <span className="text-[#C1A661] text-xs">◆</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <IncludedItem icon={Globe} data={t.modal.included.origin} testid="inc-origin" />
            <IncludedItem icon={Gem} data={t.modal.included.pricecarat} testid="inc-pricecarat" />
            <IncludedItem icon={Search} data={t.modal.included.treatments} testid="inc-treatments" />
            <IncludedItem icon={FlaskConical} data={t.modal.included.synthetics} testid="inc-synthetics" />
            <IncludedItem icon={BookOpen} data={t.modal.included.sheets} testid="inc-sheets" />
          </div>
        </div>

        {/* Trust line */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border border-[#C1A661]/20 rounded-sm py-3 px-6">
          <ShieldCheck size={16} className="text-[#C1A661]" strokeWidth={1.4} />
          {t.modal.trustLine.map((line, i) => (
            <React.Fragment key={line}>
              <span className="text-[11px] md:text-xs text-[#D8DBE2] font-light">{line}</span>
              {i < t.modal.trustLine.length - 1 && <span className="text-[#C1A661] text-xs">•</span>}
            </React.Fragment>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-8 flex flex-col items-center gap-4">
          <div className="text-center text-[11px] text-[#8A8F99] font-light italic">
            {t.modal.footerNote}
          </div>
        </div>
      </div>
    </div>
  );
};

const IncludedItem = ({ icon: Icon, data, testid }) => (
  <div className="flex flex-col items-center text-center" data-testid={testid}>
    <Icon size={26} strokeWidth={1.2} className="text-[#C1A661]" />
    <div className="mt-3 text-[11px] tracking-[0.2em] uppercase text-[#E2CFA4] font-semibold">
      {data.title}
    </div>
    <p className="mt-2 text-[11px] leading-relaxed text-[#A1A7B3] font-light">
      {data.desc}
    </p>
  </div>
);
